/**
 * Product Card Dynamic Swatches
 * --------------------------------------------------------------
 * Minimal JS to swap product card media/title/link when a swatch
 * is clicked.
 */

/**
 * Handle swatch click and update card UI.
 * @param {MouseEvent} event
 */
function handleDelegatedClick(event) {
  const target = /** @type {HTMLElement|null} */ (event.target);
  if (!target) return;

  const swatchLink = /** @type {HTMLElement|null} */ (target.closest('.custom-product-card__swatch'));
  if (!swatchLink) return;

  const card = swatchLink.closest('.custom-product-card');
  if (!card) return;

  event.preventDefault();

  const newUrl = swatchLink.getAttribute('data-swatch-url') || '';
  const newTitle = swatchLink.getAttribute('data-swatch-title') || '';
  const primaryUrl = swatchLink.getAttribute('data-swatch-primary');
  const secondaryUrl = swatchLink.getAttribute('data-swatch-secondary');
  const productId = swatchLink.getAttribute('data-swatch-product-id');
  const variantId = swatchLink.getAttribute('data-swatch-variant-id');
  const price = swatchLink.getAttribute('data-swatch-price');
  const comparePrice = swatchLink.getAttribute('data-swatch-compare-price');
  const availableAttr = swatchLink.getAttribute('data-swatch-available');
  const isVariantAvailable = availableAttr === null ? true : availableAttr === 'true';

  const isInCartDrawer = card.closest('.cart-drawer__upsell') !== null;

  // Update link
  if (newUrl) card.setAttribute('href', newUrl);
  const titleEl = card.querySelector('.custom-product-card__title');
  if (titleEl && newTitle) titleEl.replaceChildren(document.createTextNode(newTitle));

  if (isInCartDrawer) {
    // Update price if provided
    if (price) {
      const priceEl = card.querySelector('.custom-product-card__price-row .price__regular');
      if (priceEl) {
        priceEl.textContent = price;
      }

      // Update compare at price if exists
      const comparePriceEl = card.querySelector('.custom-product-card__price-row .price__sale');
      if (comparePrice && comparePriceEl) {
        comparePriceEl.textContent = comparePrice;
        comparePriceEl.style.display = '';
      } else if (comparePriceEl) {
        comparePriceEl.style.display = 'none';
      }
    }

    // Update quick-add form if present
    if (productId && variantId) {
      const quickAddForm = card.querySelector('form[data-type="add-to-cart-form"]');
      if (quickAddForm) {
        // Update form action to new product
        const formAction = `/cart/add`;
        quickAddForm.setAttribute('action', formAction);

        // Update variant ID hidden input
        const variantInput = quickAddForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variantId;
          // Enable the input if it was disabled
          if (isVariantAvailable) {
            variantInput.removeAttribute('disabled');
          } else {
            variantInput.setAttribute('disabled', 'disabled');
          }
        }
      }
    }


    const quickAddButton = card.querySelector('.quick-add__button');
    if (quickAddButton) {
      const defaultText = quickAddButton.getAttribute('data-add-text') || 'Add';
      const soldOutText = quickAddButton.getAttribute('data-sold-out-text') || 'Sold out';
      const textContent = quickAddButton.querySelector('.add-to-cart-text__content');

      if (isVariantAvailable) {
        quickAddButton.removeAttribute('disabled');
        quickAddButton.removeAttribute('aria-disabled');
        quickAddButton.classList.remove('is-sold-out');
        quickAddButton.classList.remove('atc-added');
        if (textContent) {
          textContent.textContent = defaultText;
        } else {
          // Static button - update button text directly
          quickAddButton.textContent = defaultText;
        }
      } else {
        quickAddButton.setAttribute('disabled', 'disabled');
        quickAddButton.setAttribute('aria-disabled', 'true');
        quickAddButton.classList.add('is-sold-out');
        quickAddButton.classList.remove('atc-added');
        if (textContent) {
          textContent.textContent = soldOutText;
        } else {
          // Static button - update button text directly
          quickAddButton.textContent = soldOutText;
        }
      }
    }
  }

  // Update images (robustly handle picture/img outputs from responsive-image)
  const primaryWrap = card.querySelector('.custom-product-card__img-wrap--primary');
  if (primaryWrap && primaryUrl) {
    primaryWrap.querySelectorAll('img').forEach((img /** @type {HTMLImageElement} */) => {
      img.src = primaryUrl;
      img.srcset = primaryUrl;
    });
    primaryWrap.querySelectorAll('source').forEach((source /** @type {HTMLSourceElement} */) => {
      source.srcset = primaryUrl;
    });
  }

  const secondaryWrap = card.querySelector('.custom-product-card__img-wrap--secondary');
  if (secondaryWrap) {
    if (secondaryUrl) {
      secondaryWrap.querySelectorAll('img').forEach((img /** @type {HTMLImageElement} */) => {
        img.src = secondaryUrl;
        img.srcset = secondaryUrl;
      });
      secondaryWrap.querySelectorAll('source').forEach((source /** @type {HTMLSourceElement} */) => {
        source.srcset = secondaryUrl;
      });
      secondaryWrap.classList.remove('is-hidden');
    } else {
      secondaryWrap.classList.add('is-hidden');
    }
  }

  // Active state
  const container = swatchLink.closest('.custom-product-card__swatches');
  if (container) {
    container.querySelectorAll('.custom-product-card__swatch.is-active').forEach((el /** @type {Element} */) => el.classList.remove('is-active'));
    swatchLink.classList.add('is-active');
  }
}

(function attachDelegatedSwatchListener() {
  if (typeof document === 'undefined') return;
  if (document.documentElement.hasAttribute('data-swatch-delegation')) return;
  document.addEventListener('click', handleDelegatedClick, { passive: false });
  document.documentElement.setAttribute('data-swatch-delegation', '');
})();

export { };
